import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TimeMachine from "./TimeMachine";
import { WhatsAppGlyph } from "./WhatsAppButton";
import { whatsappUrl } from "../config/contact";
import { pathInLanguage, switchLanguage } from "../i18n";

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.documentElement.lang = i18n.language === "pt" ? "pt-BR" : "en";
  }, [i18n.language]);

  // Any navigation (including browser back) closes the mobile sheet.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  // Each language has its own URL; real links keep the switch crawlable.
  const changeLanguage = (lng) => (e) => {
    e.preventDefault();
    switchLanguage(lng);
  };

  const handleLinkClick = () => {
    setMenuOpen(false);
  };

  const linkClass = ({ isActive }) => (isActive ? "active" : "");

  return (
    <nav className={scrolled ? "navbar is-scrolled" : "navbar"}>
      <div className="container">
        <NavLink to="/" className="logo" onClick={handleLinkClick}>
          <span className="logo-text">{t("my_portfolio")}</span>
        </NavLink>

        <ul id="site-menu" className={menuOpen ? "nav-links open" : "nav-links"}>
          <li><NavLink to="/portfolio" className={linkClass} onClick={handleLinkClick}>{t("portfolio")}</NavLink></li>
          <li><NavLink to="/blog" className={linkClass} onClick={handleLinkClick}>{t("blog")}</NavLink></li>
          <li><NavLink to="/about" className={linkClass} onClick={handleLinkClick}>{t("about")}</NavLink></li>
          <li><NavLink to="/contact" className={linkClass} onClick={handleLinkClick}>{t("contact")}</NavLink></li>
          {menuOpen && (
            <li className="nav-mobile-extras">
              <a className="page-btn page-btn-primary page-btn-wa" href={whatsappUrl(i18n.language)} target="_blank" rel="noopener noreferrer">
                <WhatsAppGlyph /> {t("wa_label")}
              </a>
              <TimeMachine compact />
            </li>
          )}
        </ul>

        <div className="navbar-actions">
          <TimeMachine />
          <div className="language-switcher" role="group" aria-label={t("language")}>
            <a href={pathInLanguage("en")} hrefLang="en" onClick={changeLanguage("en")} className={i18n.language === "en" ? "active" : ""} aria-current={i18n.language === "en" ? "true" : undefined}>EN</a>
            <a href={pathInLanguage("pt")} hrefLang="pt-BR" onClick={changeLanguage("pt")} className={i18n.language === "pt" ? "active" : ""} aria-current={i18n.language === "pt" ? "true" : undefined}>PT</a>
          </div>

          <button
            type="button"
            className={menuOpen ? "menu-toggle active" : "menu-toggle"}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-expanded={menuOpen}
            aria-controls="site-menu"
            aria-label={menuOpen ? t("close_menu") : t("open_menu")}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
